<template>
  <div>
    <header class="site-header">
      <router-link to="/home" class="brand" @click="drawer = false">
        <img :src="logo" alt="3D Auto Centering">
        <span>3D Auto Centering</span>
      </router-link>

      <nav class="desktop-nav" aria-label="Main navigation">
        <router-link
          v-for="item in menuItems"
          :key="item.path"
          :to="item.path"
          class="nav-link"
        >
          {{ $t(item.name) }}
        </router-link>
      </nav>

      <div class="header-actions">
        <button
          v-for="language in languages"
          :key="language.value"
          class="flag-button"
          :class="{ active: $i18n.locale === language.value }"
          type="button"
          :aria-label="language.label"
          @click="changeLanguage(language.value)"
        >
          <img :src="language.flag" :alt="language.label">
        </button>

        <button
          class="menu-button"
          type="button"
          :aria-label="drawer ? 'Close menu' : 'Open menu'"
          @click="drawer = !drawer"
        >
          <v-icon>{{ drawer ? 'mdi-close' : 'mdi-menu' }}</v-icon>
        </button>
      </div>
    </header>

    <transition name="mobile-menu">
      <div v-if="drawer" class="mobile-menu">
        <router-link
          v-for="item in menuItems"
          :key="item.path"
          :to="item.path"
          class="mobile-link"
          @click="drawer = false"
        >
          <v-icon>{{ item.icon }}</v-icon>
          <span>{{ $t(item.name) }}</span>
        </router-link>

        <a class="mobile-cta" href="tel:+38975371855">
          <v-icon>mdi-phone</v-icon>
          <span>+389 75 371 855</span>
        </a>
      </div>
    </transition>

    <main class="site-main">
      <router-view />
    </main>
  </div>
</template>

<script>
import alFlag from '/public/flags/al.svg';
import mkFlag from '/public/flags/mk.svg';
import gbFlag from '/public/flags/gb.svg';
import Logo from '/public/Images/3dCenteringLogo.svg';

export default {
  data: () => ({
    drawer: false,
    logo: Logo,
    menuItems: [
      { icon: 'mdi-home', name: 'navigation.home', path: '/home' },
      { icon: 'mdi-car-cog', name: 'navigation.services', path: '/services' },
      { icon: 'mdi-card-account-phone', name: 'navigation.contact', path: '/contact' },
      { icon: 'mdi-car-multiple', name: 'navigation.gallery', path: '/gallery' },
      { icon: 'mdi-cash-multiple', name: 'navigation.prices', path: '/prices' },
    ],
    languages: [
      { value: 'mkd', label: 'Македонски', flag: mkFlag },
      { value: 'en', label: 'English', flag: gbFlag },
      { value: 'al', label: 'Shqip', flag: alFlag },
    ],
  }),

  methods: {
    changeLanguage(value) {
      this.$i18n.locale = value;
      localStorage.setItem('language', value);
      this.drawer = false;
    },
  },
};
</script>

<style scoped>
.site-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 20;
  display: flex;
  min-height: 72px;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(7, 9, 11, 0.84);
  padding: 0 clamp(14px, 4vw, 46px);
  backdrop-filter: blur(18px);
}

.brand {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  color: white;
  font-size: 0.98rem;
  font-weight: 950;
  text-decoration: none;
  text-transform: uppercase;
}

.brand img {
  width: 46px;
  height: 46px;
  object-fit: contain;
}

.desktop-nav {
  display: flex;
  align-items: center;
  gap: 4px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 5px;
  background: rgba(255, 255, 255, 0.06);
}

.nav-link {
  border-radius: 7px;
  padding: 10px 14px;
  color: #dce1e7;
  font-size: 0.9rem;
  font-weight: 800;
  text-decoration: none;
  transition: background 0.2s ease, color 0.2s ease;
}

.nav-link:hover,
.nav-link.router-link-active {
  background: var(--site-orange);
  color: white;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.flag-button,
.menu-button {
  display: inline-grid;
  width: 40px;
  height: 40px;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.06);
  color: white;
  cursor: pointer;
}

.flag-button.active {
  border-color: var(--site-orange);
  background: rgba(255, 122, 0, 0.18);
}

.flag-button img {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
}

.menu-button {
  display: none;
}

.site-main {
  min-height: 100vh;
}

.mobile-menu {
  position: fixed;
  top: 72px;
  left: 12px;
  right: 12px;
  z-index: 19;
  display: grid;
  gap: 8px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  padding: 12px;
  background: rgba(9, 11, 14, 0.96);
  box-shadow: 0 26px 90px rgba(0, 0, 0, 0.42);
  backdrop-filter: blur(18px);
}

.mobile-link,
.mobile-cta {
  display: flex;
  min-height: 50px;
  align-items: center;
  gap: 12px;
  border-radius: 8px;
  padding: 0 14px;
  color: white;
  font-weight: 850;
  text-decoration: none;
}

.mobile-link {
  background: rgba(255, 255, 255, 0.06);
}

.mobile-link.router-link-active,
.mobile-cta {
  background: var(--site-orange);
}

.mobile-menu-enter-active,
.mobile-menu-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.mobile-menu-enter-from,
.mobile-menu-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

@media (max-width: 1020px) {
  .desktop-nav {
    display: none;
  }

  .menu-button {
    display: inline-grid;
  }
}

@media (max-width: 560px) {
  .site-header {
    min-height: 64px;
    padding-inline: 12px;
  }

  .brand span {
    max-width: 130px;
    font-size: 0.78rem;
    line-height: 1.05;
  }

  .brand img {
    width: 40px;
    height: 40px;
  }

  .flag-button,
  .menu-button {
    width: 36px;
    height: 36px;
  }

  .mobile-menu {
    top: 64px;
  }
}
</style>
